import type { IDestDefinition } from '@stores/destinationDefsList';
import { isResourceHiddenByOptions, type FeatureFlagsList } from '../definitionGates';

type Definition = Pick<IDestDefinition, 'version' | 'versions' | 'options'>;

/** Resolve a major against that major's own hidden gate and archive support status. */
export const isMajorAvailable = (
  definition: Definition,
  major: number,
  flags: FeatureFlagsList,
) => {
  if (definition.version === major) {
    return !isResourceHiddenByOptions(definition.options, flags);
  }
  const archived = definition.versions?.find((entry) => entry.version === major);
  return archived?.status === 'supported' && !isResourceHiddenByOptions(archived.options, flags);
};

const createMajor = (definition: Definition, flags: FeatureFlagsList): number | undefined => {
  const majors = [definition.version, ...(definition.versions ?? []).map((v) => v.version)].filter(
    (major): major is number => major !== undefined && isMajorAvailable(definition, major, flags),
  );
  return majors.length > 0 ? Math.max(...majors) : undefined;
};

const getCreateDefinition = <T extends IDestDefinition>(
  definition: T,
  flags: FeatureFlagsList,
): T | undefined => {
  const major = createMajor(definition, flags);
  if (major === undefined) {
    return definition.version === undefined &&
      !definition.versions?.length &&
      !isResourceHiddenByOptions(definition.options, flags)
      ? definition
      : undefined;
  }
  if (major === definition.version) {
    return definition;
  }
  const archived = definition.versions?.find((entry) => entry.version === major)!;
  return {
    ...definition,
    version: major,
    config: archived.config,
    uiConfig: archived.uiConfig,
    options: archived.options,
  };
};

/** The creatable definitions, each resolved to its create major; unavailable ones are dropped. */
export const getCreateDefinitions = <T extends IDestDefinition>(
  definitions: T[],
  flags: FeatureFlagsList,
): T[] => definitions.flatMap((definition) => getCreateDefinition(definition, flags) ?? []);
